/** Accès aux données du module ventes : uniquement via le client API (contrat OpenAPI). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Page, S } from '@myron/contracts';
import { del, get, patch, post } from '../../shared/api/client';

export type TypeCond = 'PALETTE' | 'CARTON' | 'PACK';
export type ProduitPublic = S<'ProduitPublic'>;
export type ProduitPro = S<'ProduitPro'>;
export type Offre = NonNullable<ProduitPro['offres']>[number];
export type LignePanier = S<'LigneCalculee'> & {
  id: string; libelle: string; sku: string; unitesBase: number; minimum: number; multiple: number;
  alerte?: 'MINIMUM_NON_ATTEINT' | 'MULTIPLE_INVALIDE' | 'NON_VENDABLE' | 'PRODUIT_ARCHIVE' | 'QUANTITE_HORS_ACCORD';
  promotion?: boolean;
  accord?: { reference: string; quantiteMin: number | null; quantiteMax: number | null };
  lienDevis?: string;
};
export type Panier = Omit<S<'Panier'>, 'lignes'> & { lignes: LignePanier[]; volume?: { palettes: number; seuilDevisPalettes: number; devisRecommande: boolean } };
export type Devis = Omit<S<'Devis'>, 'lignes'> & { minimumCommandeHtCentimes?: number; lignes: (S<'LigneCalculee'> & { libelle?: string; sku?: string })[] };
export type CommandeResume = S<'CommandeResume'>;
export type Commande = Omit<S<'Commande'>, 'documents' | 'paiements'> & {
  ibanReception?: string; codeRetrait?: string; choixStockInsuffisant?: string;
  documents?: { id: string; type: string; numero?: string; creeLe?: string }[];
  paiements?: (S<'Paiement'> & { acompte?: boolean })[];
};
export type Facture = S<'Facture'>;
export type EntrepotPublic = S<'EntrepotPublic'>;
export type Choix = S<'ChoixRemiseEtPaiement'>;

export const cles = {
  catalogue: (p: Record<string, unknown>) => ['pro', 'catalogue', p] as const,
  produit: (id: string) => ['pro', 'produit', id] as const,
  panier: ['pro', 'panier'] as const,
  commandes: (p: Record<string, unknown>) => ['pro', 'commandes', p] as const,
  commande: (id: string) => ['pro', 'commande', id] as const,
  entrepots: ['pro', 'entrepots'] as const,
};

export const useEntrepots = () => useQuery({ queryKey: cles.entrepots, queryFn: () => get<EntrepotPublic[]>('/pro/entrepots'), staleTime: 300_000 });
export const usePanier = (actif = true) => useQuery({ queryKey: cles.panier, queryFn: () => get<Panier>('/pro/panier'), enabled: actif });
export const useCommande = (id: string) => useQuery({ queryKey: cles.commande(id), queryFn: () => get<Commande>(`/pro/commandes/${id}`) });
export const useCatalogue = (p: Record<string, string | number | undefined>) =>
  useQuery({ queryKey: cles.catalogue(p), queryFn: () => get<Page<ProduitPro>>('/pro/catalogue/produits', p), placeholderData: (x) => x });

/** Mutations du panier : chaque réponse renvoie le panier recalculé par le serveur. */
export function useMutationsPanier() {
  const qc = useQueryClient();
  const maj = (p: Panier | undefined) => { if (p) qc.setQueryData(cles.panier, p); };
  return {
    ajouter: useMutation({ mutationFn: (l: { produitId: string; conditionnementType: TypeCond; quantite: number }) => post<Panier>('/pro/panier/lignes', l), onSuccess: maj }),
    modifier: useMutation({ mutationFn: ({ id, quantite }: { id: string; quantite: number }) => patch<Panier>(`/pro/panier/lignes/${id}`, { quantite }), onSuccess: maj }),
    supprimer: useMutation({ mutationFn: (id: string) => del<Panier>(`/pro/panier/lignes/${id}`), onSuccess: maj }),
    vider: useMutation({ mutationFn: () => del('/pro/panier'), onSuccess: () => qc.invalidateQueries({ queryKey: cles.panier }) }),
  };
}

export const LIBELLE_SOURCE: Record<string, string> = { CLIENT: 'Tarif négocié', GROUPE: 'Tarif de votre groupe', BASE: 'Prix catalogue' };
export const LIBELLE_ALERTE: Record<string, string> = {
  MINIMUM_NON_ATTEINT: 'Quantité inférieure au minimum de ce conditionnement',
  MULTIPLE_INVALIDE: 'Quantité non multiple du conditionnement',
  NON_VENDABLE: 'Ce conditionnement n’est plus vendu',
  PRODUIT_ARCHIVE: 'Produit retiré du catalogue',
  QUANTITE_HORS_ACCORD: 'Quantité hors des limites de votre accord',
};

/** Erreur orientée devis (QUANTITE_HORS_ACCORD, MARGE_INSUFFISANTE, zone non desservie) : lien vers la demande de devis. */
export function lienDevisErreur(e: unknown): string | null {
  const p = (e as { probleme?: Record<string, unknown> } | null)?.probleme;
  if (!p?.devisPossible) return null;
  return typeof p.lienDevis === 'string' ? p.lienDevis : '/pro/devis/nouveau';
}
export const LIBELLE_UNITE: Record<string, string> = { BOUTEILLE: 'bouteilles', CANETTE: 'canettes', BRIQUE: 'briques', PAQUET: 'paquets', BOCAL: 'bocaux', KG: 'kg', L: 'litres', PIECE: 'pièces' };

/** « 84 cartons (6 couches × 14) · 2 016 bouteilles » */
export function detailConditionnement(c: { type: string; unitesBase?: number; cartonsParCouche?: number; couchesParPalette?: number }, uniteBase?: string) {
  const u = `${new Intl.NumberFormat('fr-FR').format(c.unitesBase ?? 0)} ${LIBELLE_UNITE[uniteBase ?? ''] ?? 'unités'}`;
  if (c.type === 'PALETTE' && c.cartonsParCouche && c.couchesParPalette) return `${c.cartonsParCouche * c.couchesParPalette} cartons (${c.couchesParPalette} couches × ${c.cartonsParCouche}) · ${u}`;
  return u;
}
